import { Paths } from '@cdn/navigation';

interface Specs {
	/** Origin of this application */
	own: string;
	/** Other origins a sign-in may return to, such as Workspace */
	origins?: readonly string[];
	storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

/**
 * Where to go after signing in. A requested destination is preserved through the sign-in and validated again
 * before it is followed: only paths of this application's route table, or absolute URLs of explicitly allowed
 * origins, are accepted. Anything else becomes the home path. A saved destination is never an access grant:
 * the destination's own screen still checks the organization and the resource.
 */
export class Destination {
	static readonly home = Paths.patterns.home;
	static readonly #key = 'beyond.cdn.return';
	// The addresses outside an organization come from the route table, so a new one is a destination once it is a route
	static readonly #allowed = [
		/^\/$/,
		...[Paths.patterns.waitlist, Paths.patterns.notifications].map(path => new RegExp(`^${path}/?$`)),
		/^\/o\/[A-Za-z0-9][A-Za-z0-9_.-]{0,127}(\/[A-Za-z0-9_.~%-]+)*\/?$/
	];

	#own: string;
	#origins: readonly string[];
	#storage: Specs['storage'];

	constructor({ own, origins = [], storage }: Specs) {
		this.#own = own;
		this.#origins = origins;
		this.#storage = storage;
	}

	/**
	 * @returns A destination that is safe to follow: an application path, an allowed absolute URL, or home
	 */
	validate(raw: unknown): string {
		if (typeof raw !== 'string' || !raw || raw.length > 2048) return Destination.home;
		// Control characters and backslashes are how browsers are tricked into another host
		if (raw.includes('\\') || [...raw].some(character => character.charCodeAt(0) < 32)) return Destination.home;

		let url: URL;
		try {
			url = new URL(raw, this.#own);
		} catch {
			return Destination.home;
		}
		if (url.username || url.password || !/^https?:$/.test(url.protocol)) return Destination.home;

		if (url.origin !== this.#own) return !raw.startsWith('/') && this.#origins.includes(url.origin) ? url.href : Destination.home;
		// `//host` is scheme-relative in browsers: only a single leading slash is a path
		if (!raw.startsWith('/') || raw.startsWith('//')) return Destination.home;
		if (!Destination.#allowed.some(pattern => pattern.test(url.pathname))) return Destination.home;
		return `${url.pathname}${url.search}${url.hash}`;
	}

	/**
	 * true for a destination outside this application, which needs a full navigation
	 */
	external(destination: string) {
		return !destination.startsWith('/');
	}

	/**
	 * Rechecks a validated destination against the organizations of the signed-in account
	 */
	permitted(destination: string, organizations: readonly string[]): string {
		const scope = /^\/o\/([^/]+)/.exec(destination)?.[1];
		return scope && !organizations.includes(decodeURIComponent(scope)) ? Destination.home : destination;
	}

	/** Keeps the destination while the browser visits an identity provider */
	remember(raw: unknown) {
		try {
			this.#storage?.setItem(Destination.#key, this.validate(raw));
		} catch {
			// Without storage the sign-in returns home
		}
	}

	/** Reads the kept destination once and validates it again: storage is not trusted */
	take(): string {
		try {
			const kept = this.#storage?.getItem(Destination.#key);
			this.#storage?.removeItem(Destination.#key);
			return this.validate(kept);
		} catch {
			return Destination.home;
		}
	}
}
